import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api'
import { getImageUrl } from '../config'

interface Category {
  id: number
  name: string
  type: string
}

interface FormData {
  title: string
  slug: string
  thumbnail: string
  logo: string
  summary: string
  industry_category_id: string
  region_category_id: string
  hero_title: string
  hero_content: string
  hero_bg: string
  content: string
  pdf_images: string
  pdf_download_url: string
  publish_date: string
  is_published: boolean
  sort_order: number
}

const emptyForm: FormData = {
  title: '', slug: '', thumbnail: '', logo: '', summary: '',
  industry_category_id: '', region_category_id: '',
  hero_title: '', hero_content: '', hero_bg: '', content: '', pdf_images: '', pdf_download_url: '',
  publish_date: '', is_published: false, sort_order: 0,
}

export default function ReportEditPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id
  const [form, setForm] = useState<FormData>(emptyForm)
  const [categories, setCategories] = useState<Category[]>([])
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [pdfUploading, setPdfUploading] = useState(false)
  const pdfInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    api.get('/api/admin/categories').then((res) => {
      if (res.success) setCategories(res.data)
    })
    if (isEdit) {
      api.get(`/api/admin/reports/${id}`).then((res) => {
        if (res.success && res.data) {
          const d = res.data
          setForm({
            title: d.title || '', slug: d.slug || '',
            thumbnail: d.thumbnail || '', logo: d.logo || '',
            summary: d.summary || '',
            industry_category_id: d.industry_category_id ? String(d.industry_category_id) : '',
            region_category_id: d.region_category_id ? String(d.region_category_id) : '',
            hero_title: d.hero_title || '', hero_content: d.hero_content || '',
            hero_bg: d.hero_bg || '',
            content: d.content || '',
            pdf_images: d.pdf_images || '',
            pdf_download_url: d.pdf_download_url || '',
            publish_date: d.publish_date || '',
            is_published: !!d.is_published,
            sort_order: d.sort_order || 0,
          })
        }
      })
    }
  }, [id])

  const update = (key: keyof FormData, value: string | boolean | number) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleUpload = async (file: File, field: 'thumbnail' | 'logo' | 'pdf_images' | 'hero_bg') => {
    setUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    const res = await api.post('/api/admin/upload', formData)
    if (res.success && res.data?.url) {
      update(field, res.data.url)
    } else {
      alert(res.error || '上传失败')
    }
    setUploading(false)
  }

  const handlePdfDownloadUpload = async (file: File) => {
    setPdfUploading(true)
    const formData = new FormData()
    formData.append('file', file)
    const res = await api.post('/api/admin/upload', formData)
    if (res.success && res.data?.url) {
      update('pdf_download_url', res.data.url)
    } else {
      alert(res.error || '上传失败')
    }
    setPdfUploading(false)
    if (pdfInputRef.current) pdfInputRef.current.value = ''
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim()) {
      alert('请输入标题')
      return
    }

    setSaving(true)
    const body = {
      ...form,
      industry_category_id: form.industry_category_id ? Number(form.industry_category_id) : null,
      region_category_id: form.region_category_id ? Number(form.region_category_id) : null,
      pdf_images: form.pdf_images || null,
    }

    const res = isEdit
      ? await api.put(`/api/admin/reports/${id}`, body)
      : await api.post('/api/admin/reports', body)

    if (res.success) {
      navigate('/reports')
    } else {
      alert(res.error || '保存失败')
    }
    setSaving(false)
  }

  const industries = categories.filter((c) => c.type === 'industry')
  const regions = categories.filter((c) => c.type === 'region')

  const renderOpts = (cats: any[]) => cats.map((c: any) => {
    if (c.children?.length > 0) {
      return (
        <optgroup key={c.id} label={c.name}>
          <option value={c.id}>{c.name}</option>
          {c.children.map((child: any) => (
            <option key={child.id} value={child.id}>&nbsp;&nbsp;└ {child.name}</option>
          ))}
        </optgroup>
      )
    }
    return <option key={c.id} value={c.id}>{c.name}</option>
  })

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{isEdit ? '编辑报告' : '新增报告'}</h1>
      </div>

      <form onSubmit={handleSubmit} style={{ maxWidth: '720px' }}>
        <div style={{ background: 'white', padding: '24px', borderRadius: '8px', marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>基本信息</h3>
          <div className="form-group">
            <label className="form-label">标题 *</label>
            <input className="form-input" value={form.title} onChange={(e) => update('title', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Slug</label>
            <input className="form-input" value={form.slug} onChange={(e) => update('slug', e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">行业分类</label>
              <select className="form-input" value={form.industry_category_id} onChange={(e) => update('industry_category_id', e.target.value)}>
                <option value="">请选择</option>
                {renderOpts(industries)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">地区分类</label>
              <select className="form-input" value={form.region_category_id} onChange={(e) => update('region_category_id', e.target.value)}>
                <option value="">请选择</option>
                {renderOpts(regions)}
              </select>
            </div>
          </div>
        </div>

        <div style={{ background: 'white', padding: '24px', borderRadius: '8px', marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>图片</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">缩略图</label>
              {form.thumbnail && <img src={getImageUrl(form.thumbnail)} alt="" style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '6px', marginBottom: '8px' }} />}
              <input className="form-input" value={form.thumbnail} onChange={(e) => update('thumbnail', e.target.value)} placeholder="URL 或上传" />
              <input type="file" accept="image/*" style={{ marginTop: '8px' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f, 'thumbnail') }} />
            </div>
            <div className="form-group">
              <label className="form-label">Logo</label>
              {form.logo && <img src={getImageUrl(form.logo)} alt="" style={{ width: '100%', maxHeight: '160px', objectFit: 'contain', borderRadius: '6px', marginBottom: '8px' }} />}
              <input className="form-input" value={form.logo} onChange={(e) => update('logo', e.target.value)} placeholder="URL 或上传" />
              <input type="file" accept="image/*" style={{ marginTop: '8px' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f, 'logo') }} />
            </div>
          </div>
          {uploading && <p style={{ color: 'var(--color-primary)', fontSize: '13px' }}>上传中...</p>}
        </div>

        <div style={{ background: 'white', padding: '24px', borderRadius: '8px', marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>Hero 区域</h3>
          <div className="form-group">
            <label className="form-label">Hero 标题</label>
            <input className="form-input" value={form.hero_title} onChange={(e) => update('hero_title', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Hero 内容</label>
            <textarea className="form-input" value={form.hero_content} onChange={(e) => update('hero_content', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Hero 背景图</label>
            {form.hero_bg && (
              <img src={getImageUrl(form.hero_bg)} alt="" style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '6px', marginBottom: '8px' }} />
            )}
            <input className="form-input" value={form.hero_bg} onChange={(e) => update('hero_bg', e.target.value)} placeholder="URL 或上传（留空使用默认背景）" />
            <input type="file" accept="image/*" style={{ marginTop: '8px' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f, 'hero_bg') }} />
          </div>
        </div>

        <div style={{ background: 'white', padding: '24px', borderRadius: '8px', marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>正文内容</h3>

          <div className="form-group">
            <label className="form-label">PDF 图片</label>
            {form.pdf_images && (
              <div style={{ margin: '0 -24px', overflow: 'hidden' }}>
                <img src={getImageUrl(form.pdf_images)} alt="" style={{ width: '100%', display: 'block' }} />
              </div>
            )}
            <input className="form-input" value={form.pdf_images} onChange={(e) => update('pdf_images', e.target.value)} placeholder="URL 或上传" />
            <input type="file" accept="image/*" style={{ marginTop: '8px' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f, 'pdf_images' as any) }} />
          </div>
          <div className="form-group">
            <label className="form-label">PDF 下载链接</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                className="form-input"
                style={{ flex: 1 }}
                value={form.pdf_download_url}
                onChange={(e) => update('pdf_download_url', e.target.value)}
                placeholder="https://... 或上传 PDF 文件"
              />
              <label style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '0 14px',
                background: 'var(--color-primary)',
                color: '#fff',
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                height: '44px',
              }}>
                {pdfUploading ? '上传中...' : '上传 PDF'}
                <input
                  ref={pdfInputRef}
                  type="file"
                  accept=".pdf"
                  disabled={pdfUploading}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) handlePdfDownloadUpload(f)
                  }}
                />
              </label>
            </div>
          </div>
        </div>

        <div style={{ background: 'white', padding: '24px', borderRadius: '8px', marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '16px', fontSize: '16px' }}>发布设置</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label className="form-label">发布日期</label>
              <input className="form-input" type="date" value={form.publish_date} onChange={(e) => update('publish_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">排序</label>
              <input className="form-input" type="number" value={form.sort_order} onChange={(e) => update('sort_order', Number(e.target.value))} />
            </div>
            <div className="form-group">
              <label className="form-label">状态</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                <input type="checkbox" checked={form.is_published} onChange={(e) => update('is_published', e.target.checked)} />
                已发布
              </label>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
          <button type="button" className="btn btn-outline" onClick={() => navigate('/reports')}>取消</button>
        </div>
      </form>
    </div>
  )
}
